import { AlgoliaProductCardRenderer } from "@lyle/algolia-product-card";
import { AlgoliaProductFetcher } from "@lyle/algolia-product-fetcher";
import {
  createMoneyFormatter,
  formatMoney,
  parseJson,
} from "@lyle/algolia-product-utils";

/**
 * Hydrates server-rendered product cards (e.g. featured products) with the
 * same Algolia-powered colour swatches, variant links and quick view used
 * by the collection grid, without touching the collection controller.
 *
 * Each card must expose a `[data-product-card-json]` script. Swatches load
 * lazily as cards scroll into view; sizes render immediately from the variants
 * already serialised in the card JSON.
 */
class LyleProductCards extends HTMLElement {
  #abortController = null;
  #cardRenderer = null;
  #config = null;
  #fetcher = null;
  #hydratedSwatches = new WeakSet();
  #icons = {};
  #intersectionObserver = null;
  #moneyFormatter = null;
  #quickView = null;
  #quickViewPromise = null;
  #swatchObserver = null;

  connectedCallback() {
    this.#config = this.#parseConfig();

    if (!this.#config) {
      return;
    }

    this.#abortController = new AbortController();
    this.#icons = window.icons || {};
    this.#moneyFormatter = createMoneyFormatter({
      currencyCode: this.#config.market.currencyCode,
      language: this.#config.market.language,
    });
    this.#fetcher = new AlgoliaProductFetcher({
      config: this.#config,
      signal: this.#abortController.signal,
    });
    this.#cardRenderer = new AlgoliaProductCardRenderer({
      config: this.#config,
      icons: this.#icons,
      isMobile: () => this.#isMobile(),
    });

    this.#bindEvents();

    if (!("IntersectionObserver" in window)) {
      void this.hydrate();
      return;
    }

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
      void this.hydrate();
    }, { rootMargin: "400px 0px" });
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#swatchObserver?.disconnect();
  }

  async hydrate(scope = this) {
    const cardProducts = new Map();
    const familyTags = [];
    const handles = [];

    const cards = scope.matches?.("[data-product-card]")
      ? [scope]
      : scope.querySelectorAll("[data-product-card]");

    cards.forEach((card) => {
      const product = this.#cardProduct(card);

      if (!product) {
        return;
      }

      const familyTag = this.#fetcher.familyTag(product);

      if (familyTag) {
        familyTags.push(familyTag);
      }

      if (card.querySelector("[data-card-product]")) {
        this.#observeCardSwatches(card, product);
        return;
      }

      cardProducts.set(card, product);

      if (product.handle) {
        handles.push(product.handle);
      }
    });

    if (familyTags.length) {
      void this.#fetcher.fetchRelatedProductsBatch(familyTags);
    }

    await this.#renderFromAlgolia(cardProducts, handles);
  }

  #parseConfig() {
    const script = this.querySelector("[data-product-cards-config]");

    if (!script?.textContent) {
      return null;
    }

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  #bindEvents() {
    this.addEventListener("click", this.#handleClick, {
      signal: this.#abortController.signal,
    });

    if (this.#config.quickView) {
      this.querySelector("[data-quick-view-overlay]")?.addEventListener(
        "toggle",
        (event) => this.#quickView?.handleToggle(event),
        { signal: this.#abortController.signal },
      );
    }
  }

  #handleClick = (event) => {
    const swatch = event.target.closest("[data-card-swatch]");
    const quickView = event.target.closest("[data-quick-view]");
    const quickViewClose = event.target.closest("[data-quick-view-close]");
    const quickViewAdd = event.target.closest("[data-quick-view-add]");
    const quickViewArrow = event.target.closest("[data-quick-view-arrow]");
    const quickViewSize = event.target.closest("[data-quick-view-size]");
    const quickViewSwatch = event.target.closest("[data-quick-view-swatch]");

    if (this.#config.quickView && quickView) {
      event.preventDefault();
      void this.#openQuickView(quickView);
      return;
    }

    if (this.#quickView && quickViewClose) {
      event.preventDefault();
      this.#quickView.close();
      return;
    }

    if (this.#quickView && quickViewAdd) {
      event.preventDefault();
      void this.#quickView.add(quickViewAdd);
      return;
    }

    if (this.#quickView && quickViewArrow) {
      event.preventDefault();
      this.#quickView.scrollGallery();
      return;
    }

    if (this.#quickView && quickViewSize) {
      event.preventDefault();
      this.#quickView.selectVariant(quickViewSize);
      return;
    }

    if (this.#quickView && quickViewSwatch) {
      event.preventDefault();
      void this.#quickView.selectSwatch(quickViewSwatch);
      return;
    }

    if (swatch) {
      event.preventDefault();
      this.#selectSwatch(swatch);
    }
  };

  async #openQuickView(button) {
    if (!this.#quickViewPromise) {
      this.#quickViewPromise = import("@lyle/algolia-product-card-quick-view");
    }

    const { AlgoliaProductCardQuickView } = await this.#quickViewPromise;
    if (!this.isConnected) {
      return;
    }

    if (!this.#quickView) {
      this.#quickView = new AlgoliaProductCardQuickView({
        callbacks: {
          cardProduct: (card) => this.#cardProduct(card),
          familyTag: (product) => this.#fetcher.familyTag(product),
          fetchProductJson: (handle) => this.#fetcher.fetchProductJson(handle),
          fetchRelatedProductsBatch: (tags) =>
            this.#fetcher.fetchRelatedProductsBatch(tags),
          filterColourProducts: (product, relatedProducts) =>
            this.#fetcher.filterColourProducts(product, relatedProducts),
          formatMoney: (value) => this.#formatMoney(value),
          sendConversionEvent: () => {},
        },
        config: this.#config,
        icons: this.#icons,
        root: this,
      });
    }

    await this.#quickView.open(button);
  }

  // Re-render each card from Algolia so availability, price, sizes and swatches
  // match the collection grid (Liquid `variant.available` is unreliable in
  // previews with no inventory). Falls back to the server-rendered Liquid card
  // when a product is missing from the index.
  async #renderFromAlgolia(cardProducts, handles) {
    const productsByHandle =
      await this.#fetcher.fetchProductsByHandles(handles);

    cardProducts.forEach((liquidProduct, card) => {
      if (!card.isConnected) {
        return;
      }

      const algoliaProduct = productsByHandle.get(liquidProduct.handle);

      if (algoliaProduct) {
        this.#replaceCard(card, algoliaProduct, liquidProduct);
      } else {
        this.#observeCardSwatches(card, liquidProduct);
      }
    });
  }

  #observeCardSwatches(card, product) {
    if (this.#hydratedSwatches.has(card) || !this.#fetcher.familyTag(product)) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      void this.#hydrateCardSwatches(card);
      return;
    }

    if (!this.#swatchObserver) {
      this.#swatchObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.#swatchObserver?.unobserve(entry.target);
            void this.#hydrateCardSwatches(entry.target);
          });
        },
        { rootMargin: "600px 0px" },
      );
    }

    this.#swatchObserver.observe(card);
  }

  async #hydrateCardSwatches(card) {
    if (this.#hydratedSwatches.has(card)) {
      return;
    }

    this.#hydratedSwatches.add(card);

    const product = this.#cardProduct(card);
    const familyTag = product ? this.#fetcher.familyTag(product) : "";

    if (!familyTag) {
      return;
    }

    const relatedProducts =
      (await this.#fetcher.fetchRelatedProductsBatch([familyTag])).get(
        familyTag,
      ) || [];

    if (!card.isConnected) {
      return;
    }

    this.#cardRenderer.renderSwatches(
      card,
      product,
      this.#fetcher.filterColourProducts(product, relatedProducts),
    );
  }

  #selectSwatch(swatch) {
    const card = swatch.closest("[data-product-card]");
    const product = parseJson(swatch.dataset.product);

    if (!card || !product) {
      return;
    }

    const currentProduct = this.#cardProduct(card);

    if (product.handle === currentProduct?.handle) {
      return;
    }

    this.#replaceCard(card, {
      ...product,
      __indexName: currentProduct?.__indexName || product.__indexName,
      __position: currentProduct?.__position || product.__position,
      __queryID: currentProduct?.__queryID || product.__queryID,
    });
  }

  #replaceCard(card, product, liquidProduct = null) {
    const cards = [...this.querySelectorAll("[data-product-card]")];
    const index = Math.max(cards.indexOf(card), 0);
    const template = document.createElement("template");
    const imageOptions = this.#cardImageOptions(card);
    const productToRender = this.#productWithImageAlts(product, liquidProduct);

    template.innerHTML = this.#cardRenderer
      .render(productToRender, index, imageOptions)
      .trim();

    const newCard = template.content.firstElementChild;

    if (!newCard) {
      return;
    }

    newCard.className = card.className;

    if (imageOptions.imageOrder != null) {
      newCard.dataset.imageOrder = imageOptions.imageOrder;
      newCard.dataset.useAltImage = imageOptions.useAltImage ? "true" : "false";
    }

    card.replaceWith(newCard);
    this.#observeCardSwatches(newCard, productToRender);
  }

  #cardImageOptions(card) {
    if (!card?.hasAttribute("data-image-order")) {
      return {};
    }

    return {
      imageOrder: card.dataset.imageOrder || "modal",
      useAltImage: card.dataset.useAltImage === "true",
    };
  }

  #productWithImageAlts(algoliaProduct, liquidProduct) {
    const liquidImages = liquidProduct?.images;

    if (!Array.isArray(liquidImages) || !liquidImages.length) {
      return algoliaProduct;
    }

    const altById = new Map();
    const altByPath = new Map();

    for (const image of liquidImages) {
      if (image?.id != null) {
        altById.set(String(image.id), image.alt || "");
      }

      const path = this.#imagePath(image?.src);

      if (path) {
        altByPath.set(path, image.alt || "");
      }
    }

    const mergedImages = (algoliaProduct.images || []).map((image) => {
      if (image?.alt) {
        return image;
      }

      const alt =
        altById.get(String(image?.id)) ||
        altByPath.get(this.#imagePath(image?.src)) ||
        "";

      if (!alt) {
        return image;
      }

      return { ...image, alt };
    });

    const hasAltLanding = (images) =>
      (images || []).some((image) => image?.alt?.includes("alt_landing_image"));

    return {
      ...algoliaProduct,
      images: hasAltLanding(mergedImages)
        ? mergedImages
        : hasAltLanding(liquidImages)
          ? liquidImages
          : mergedImages,
    };
  }

  #imagePath(src) {
    if (!src) {
      return "";
    }

    try {
      return new URL(src, window.location.origin).pathname.replace(
        /_(?:\d+x(?:\d+)?|crop_center|pico|icon|thumb|small|compact|medium|large|grande|master)(?=\.|$)/g,
        "",
      );
    } catch {
      return String(src).split("?")[0];
    }
  }

  #cardProduct(card) {
    const script = card?.querySelector(
      "[data-card-product], [data-product-card-json]",
    );
    return parseJson(script?.textContent);
  }

  #formatMoney(value) {
    return formatMoney(value, {
      currencySymbol: this.#config.market.currencySymbol,
      formatter: this.#moneyFormatter,
    });
  }

  #isMobile() {
    return window.matchMedia("(width < 1024px)").matches;
  }
}

if (!customElements.get("lyle-product-cards")) {
  customElements.define("lyle-product-cards", LyleProductCards);
}
